import * as d3 from 'd3';
import { useEffect, useRef } from 'react';
import { ColorPalette, UIColorPalette } from './ColorPalette';
export interface PieChartProps {
  width?: number;
  height?: number;
  title: string;
  data: { name: string; count: number }[];
  colors: string[];
  titleColor?: string;
}
export const PieChart: React.FC<PieChartProps> = ({
  width,
  height,
  title = '',
  data,
  colors = ['#FD9827', '#DA3B21', '#3669C9', '#1D9524', '#971497'],
  titleColor,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let ref = chartRef.current;
    if (ref) {
      createPieChart(ref, {
        width,
        height,
        title,
        data,
        colors,
        titleColor,
      });

      return () => {
        removePieChart(ref);
      };
    }
  }, [width, height, title, data, colors, titleColor]);

  const nameStyle = { color: titleColor };
  return (
    <div ref={chartRef}>
      <h4 className="piechart-title" style={nameStyle}>
        {title}
      </h4>
    </div>
  );
};

function createPieChart(dom: HTMLDivElement, props: PieChartProps) {
  var width = props.width ?? 100;
  var height = props.height ?? 100;
  const height_padding = 15;
  var data = props.data;
  var sum = data.reduce(function (memo, num) {
    return memo + num.count;
  }, 0);
  var chart = d3
    .select(dom)
    .append('svg')
    .attr('class', 'd3')
    .attr('width', width)
    .attr('height', height + height_padding)
    .append('g')
    .attr('transform', 'translate(' + width / 2 + ',' + height / 2 + ')');
  var outerRadius = width / 2.2;
  var innerRadius = width / 7;

  type Datum = PieChartProps['data'][number];

  var colors = props.colors;
  var pieData = d3.pie<Datum>().value(function (d) {
    return d.count;
  })(data);
  type PieDatum = (typeof pieData)[number];
  var arc = d3
    .arc<PieDatum>()
    .outerRadius(outerRadius)
    .innerRadius(innerRadius);

  var g = chart
    .selectAll('.arc')
    .data(pieData)
    .enter()
    .append('g')
    .attr('class', 'arc')
    .on('click', function (d) {
      //alert('you clicked ' + d.data.name)
    })
    .on('mouseover', function (event, i) {
      const datum = d3.select<SVGElement, PieDatum>(this).datum();
      d3.select<SVGElement, PieDatum>(this)
        .transition()
        .duration(500)
        .attr('transform', (d) => {
          var dist = 5;
          const midAngle = (d.endAngle + d.startAngle) / 2;
          var x = Math.sin(midAngle) * dist;
          var y = -Math.cos(midAngle) * dist;
          return 'translate(' + x + ',' + y + ')';
        });
      chart
        .append('text')
        .attr('id', 'percent')
        .attr('text-anchor', 'middle')
        .attr('dy', '.30em')
        .style('font', 'bold 8px Arial')
        .style('fill', ColorPalette.normalText)
        .text(`${((datum.value / sum) * 100).toFixed(0)}%`);
      chart
        .append('text')
        .attr('id', 'label')
        .attr('transform', `translate(0, ${(height + height_padding) / 2})`)
        .attr('text-anchor', 'middle')
        .attr('dy', '.30em')
        .style('font', 'bold 8px Arial')
        .style('fill', ColorPalette.normalText)
        .text(datum.data.name);

      g.filter(function (e) {
        return e != event;
      }).style('opacity', 0.7);
    })
    .on('mouseout', function (d, i) {
      d3.select<SVGElement, PieDatum>(this)
        .transition()
        .duration(500)
        // .ease('bounce')
        .attr('transform', 'translate(0,0)');
      d3.select('#percent').remove();
      d3.select('#label').remove();

      g.filter(function (e) {
        return e != d;
      }).style('opacity', 1);
    });

  g.append('path')
    .style('fill', function (d, i) {
      return colors[i];
    })
    .transition()
    .delay(function (d, i) {
      return i * 400;
    })
    .duration(400)
    .attrTween('d', (d) => {
      var i = d3.interpolate(d.startAngle, d.endAngle);
      return (t) => {
        d.endAngle = i(t);
        return arc(d)!;
      };
    });
  g.filter((d) => {
    return d.endAngle - d.startAngle > 0.1;
  })
    .append('text')
    .style('fill', UIColorPalette.eogPieChartText)
    .attr('transform', (d) => {
      return 'translate(' + arc.centroid(d) + ')';
    })
    .attr('text-anchor', 'middle')
    .attr('dy', '.35em')
    .text(function (d) {
      return d.value;
    });
}

function removePieChart(dom: HTMLDivElement): void {
  d3.select(dom).selectAll('svg').remove();
}
